import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useConfig } from "../config/ConfigContext";
import apiClient from "../lib/apiClient";
import ProductCard from "../components/product/ProductCard";
import LoadingSpinner from "../components/common/LoadingSpinner";
import SEOHead from "../components/SEOHead";
import { trackEvent } from "../components/Analytics";
import FilterSidebar from "../components/product/FilterSidebar";
import FilterDrawer from "../components/product/FilterDrawer";

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { config, isFeatureEnabled } = useConfig();

  const filters = {
    category: searchParams.get("category") || "",
    sort: searchParams.get("sort") || "newest",
  };

  useEffect(() => {
    trackEvent("page_view", { page_title: "Products" });
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiClient.getCategories();
        setCategories(response);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  // Initial Fetch (or when Filters Change)
  useEffect(() => {
    const fetchInitialProducts = async () => {
      setLoading(true);
      try {
        const apiFilters = { ...filters, page: 1, limit: 8 };
        const response = await apiClient.getProducts(apiFilters);

        if (response.products) {
          setProducts(response.products);
          setMetadata(response.pagination);
        } else {
          setProducts(response);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialProducts();
  }, [filters.category, filters.sort]);

  const handleLoadMore = async () => {
    if (!metadata || !metadata.has_next || loadingMore) return;

    setLoadingMore(true);
    try {
      const nextPage = metadata.page + 1;
      const apiFilters = { ...filters, page: nextPage, limit: 8 };
      const response = await apiClient.getProducts(apiFilters);

      if (response.products) {
        setProducts((prev) => [...prev, ...response.products]);
        setMetadata(response.pagination);
      }
    } catch (error) {
      console.error("Error loading more products:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value || value === 0) newParams.set(key, value);
    else newParams.delete(key);
    setSearchParams(newParams);
    trackEvent("filter_products", { filter_type: key, filter_value: value });
  };

  const clearAllFilters = () => {
    setSearchParams({});
    trackEvent("clear_filters", {});
  };

  const activeFilterCount = [
    filters.category,
    filters.sort !== "newest",
  ].filter(Boolean).length;

  if (loading && products.length === 0 && !config) {
    return <LoadingSpinner message="Finding your perfect products..." />;
  }

  return (
    <div
      className="min-h-screen pt-4 md:pt-24 pb-12 bg-gray-50 bg-repeat"
      style={{
        backgroundImage: "url('/images/Bella/Product%20Photos/productbackground.JPG')",
        backgroundAttachment: "fixed"
      }}
    >
      <SEOHead
        title="Products"
        description="Browse our collection of premium bedding products including mattresses, pillows, duvets and accessories."
        canonical={`${window.location.origin}/products`}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 bg-white/80 backdrop-blur-sm p-8 rounded-lg shadow-sm">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-brand-blue mb-4">
            Our Collection
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Discover premium bedding products designed for your perfect night's
            sleep
          </p>
        </div>

        {/* ... Mobile Filters (Keep existing code) ... */}
        {/* Mobile: Filter Drawer Button (Top) */}
        <div className="lg:hidden mb-6 flex items-center justify-between gap-4">
          <FilterDrawer
            filters={filters}
            onFilterChange={handleFilterChange}
            categories={categories}
            onClearAll={clearAllFilters}
            activeFilterCount={activeFilterCount}
            open={mobileFiltersOpen}
            onOpenChange={setMobileFiltersOpen}
          />
        </div>

        {/* Mobile: Floating Filter Button (FAB) */}
        <button
          onClick={() => setMobileFiltersOpen(true)}
          className="lg:hidden fixed bottom-6 right-6 z-50 bg-brand-blue hover:bg-brand-red text-white rounded-full shadow-lg p-4 transition-all duration-300"
          aria-label="Open filters"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Active Filters */}
        {(filters.category || filters.sort !== "newest") && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-8 flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-gray-700">
              Active Filters:
            </span>
            {filters.category && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-brand-blue rounded-full text-sm font-medium">
                <span>Category: {filters.category.replace(/_/g, " ")}</span>
                <button
                  onClick={() => handleFilterChange("category", "")}
                  className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                  aria-label="Remove category filter"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            )}
            {filters.sort !== "newest" && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                <span>
                  Sort:{" "}
                  {filters.sort === "price_asc"
                    ? "Price: Low to High"
                    : filters.sort === "price_desc"
                      ? "Price: High to Low"
                      : filters.sort === "name_asc"
                        ? "Name: A-Z"
                        : filters.sort === "name_desc"
                          ? "Name: Z-A"
                          : filters.sort}
                </span>
                <button
                  onClick={() => handleFilterChange("sort", "newest")}
                  className="hover:bg-amber-200 rounded-full p-0.5 transition-colors"
                  aria-label="Remove sort filter"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            )}
            <button
              onClick={clearAllFilters}
              className="ml-auto px-4 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        )}

        {/* Main Content: Sidebar + Products Grid */}
        <div className="flex gap-8 items-start">
          {/* Desktop Sidebar - Sticky */}
          <FilterSidebar
            filters={filters}
            onFilterChange={handleFilterChange}
            categories={categories}
            onClearAll={clearAllFilters}
          />

          {/* Products Grid */}
          <div className="flex-1">
            {products.length === 0 && !loading ? (
              <div className="text-center py-16">
                <div className="bg-white rounded-lg shadow-md p-12 max-w-md mx-auto">
                  <svg
                    className="w-16 h-16 text-gray-400 mx-auto mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33"
                    />
                  </svg>
                  <p className="text-gray-600 text-lg font-medium">
                    No products found matching your criteria.
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    Try adjusting your filters or search terms.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {products.map((product, index) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      index={index}
                    />
                  ))}
                </div>

                {/* Load More Button */}
                {metadata && metadata.has_next && (
                  <div className="mt-12 text-center">
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="inline-flex items-center px-8 py-3 bg-white border-2 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white rounded-full font-bold transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50"
                    >
                      {loadingMore ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Loading...
                        </>
                      ) : (
                        `Load More Products`
                      )}
                    </button>
                    <p className="mt-2 text-gray-500 text-sm">
                      Showing {products.length} of {metadata.total_count} items
                    </p>
                  </div>
                )}
              </>
            )}

            {loading && products.length === 0 && (
              <div className="py-12 text-center">
                <LoadingSpinner />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
